/**
 * Governed Domain parent-message definitions.
 *
 * Reusable message configuration lives at one central per-Domain path beneath
 * the installation. It never shares the Domain's runtime actions collection:
 * Queue follow-ups are work; these records are configuration.
 */

import { useState } from 'react';
import {
  CheckCircleIcon,
  MinusCircleIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { updateDomain } from '../../../services/domainRegistry';
import {
  createDomainMessageAction,
  deleteDomainMessageAction,
  listDomainMessageActions,
  readDomainMessageAction,
  readDomainParentMessages,
  updateDomainMessageAction,
} from '../../../services/domain/domainMessageActions';
import {
  buildDomainCapabilities,
  DOMAIN_MESSAGE_ACTION_CONTRACT,
} from '../../../services/domain/centralModel';

const EMPTY_EDITOR = {
  id: null,
  label: '',
  active: true,
  subject: '',
  body: '',
  buttons: [{ label: 'Acknowledge' }],
  makeDefault: true,
};

function actionEditorValue(action, defaultId) {
  const normalized = readDomainMessageAction(action);
  return {
    id: action.id,
    label: normalized.label,
    active: normalized.active,
    subject: normalized.message.subject,
    body: normalized.message.body,
    buttons: normalized.message.buttons.map((button) => ({
      id: button.id,
      label: button.label,
      value: button.value,
    })),
    makeDefault: action.id === defaultId,
  };
}

function Editor({ value, busy, onChange, onCancel, onSave }) {
  const update = (patch) => onChange({ ...value, ...patch });
  const updateButton = (index, label) => update({
    buttons: value.buttons.map((button, candidate) => (
      candidate === index ? { ...button, label } : button
    )),
  });

  return (
    <form className="space-y-4 rounded-xl border border-base-300 bg-base-100 p-4" onSubmit={onSave}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{value.id ? 'Edit parent message' : 'Add parent message'}</p>
          <p className="text-xs text-base-content/55 mt-0.5">
            This copy and its response buttons belong to this Domain.
          </p>
        </div>
        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={onCancel} aria-label="Close parent message editor">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="domain-message-name">Name</label>
        <input
          id="domain-message-name"
          className="input input-bordered w-full"
          value={value.label}
          onChange={(event) => update({ label: event.target.value })}
          placeholder="e.g. Parent check-in"
          required
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="domain-message-subject">Subject</label>
        <input
          id="domain-message-subject"
          className="input input-bordered w-full"
          value={value.subject}
          maxLength={DOMAIN_MESSAGE_ACTION_CONTRACT.maxSubjectLength}
          onChange={(event) => update({ subject: event.target.value })}
          placeholder="Update about {studentName}"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="domain-message-body">Message</label>
        <textarea
          id="domain-message-body"
          className="textarea textarea-bordered min-h-36 w-full"
          value={value.body}
          maxLength={DOMAIN_MESSAGE_ACTION_CONTRACT.maxBodyLength}
          onChange={(event) => update({ body: event.target.value })}
          placeholder={'Hi, a {domainName} event was recorded for {studentName}.\n\n{details}\n\nFrom: {teacherName}'}
          required
        />
        <p className="text-xs text-base-content/55">
          Available details: {DOMAIN_MESSAGE_ACTION_CONTRACT.variables.map((variable) => `{${variable}}`).join(', ')}
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Response buttons</legend>
        <p className="text-xs text-base-content/55">
          Optional, up to {DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtons}. A response is recorded on the sent message.
        </p>
        {value.buttons.map((button, index) => (
          <div key={button.id || index} className="flex items-center gap-2">
            <input
              className="input input-bordered input-sm flex-1"
              value={button.label}
              maxLength={DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtonLabelLength}
              onChange={(event) => updateButton(index, event.target.value)}
              placeholder={`Button ${index + 1}`}
              aria-label={`Response button ${index + 1}`}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-square"
              onClick={() => update({ buttons: value.buttons.filter((_, candidate) => candidate !== index) })}
              aria-label={`Remove response button ${index + 1}`}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
        {value.buttons.length < DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtons && (
          <button
            type="button"
            className="btn btn-ghost btn-sm gap-1"
            onClick={() => update({ buttons: [...value.buttons, { label: '' }] })}
          >
            <PlusIcon className="h-4 w-4" /> Add response button
          </button>
        )}
      </fieldset>

      <label className="flex items-start gap-3 cursor-pointer rounded-lg bg-base-200/60 p-3">
        <input
          type="checkbox"
          className="checkbox checkbox-primary checkbox-sm mt-0.5"
          checked={value.makeDefault}
          onChange={(event) => update({ makeDefault: event.target.checked })}
        />
        <span className="text-sm">
          <span className="font-medium block">Use for new Domain events</span>
          <span className="text-base-content/55 block mt-0.5">
            Enables automatic parent messaging for this Domain. Turning it off keeps event capture silent.
          </span>
        </span>
      </label>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !value.label.trim() || !value.body.trim()}>
          {busy && <span className="loading loading-spinner loading-xs" />}
          Save parent message
        </button>
      </div>
    </form>
  );
}

export function DomainActionsManager({ domain, schoolId }) {
  const [parentMessages, setParentMessages] = useState(() => {
    const current = readDomainParentMessages(domain);
    const definitions = current.definitions.filter((definition) => definition.retired !== true);
    return {
      ...current,
      definitions,
      defaultId: definitions.some((definition) => definition.id === current.defaultId) ? current.defaultId : null,
    };
  });
  const [actions, setActions] = useState(() => (
    listDomainMessageActions(domain).filter((definition) => definition.retired !== true)
  ));
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [editor, setEditor] = useState(null);
  const [defaultId, setDefaultId] = useState(parentMessages.defaultId);

  const persist = async (next) => {
    setSaveStatus('saving');
    const defaultDefinition = next.definitions.find(
      (definition) => definition.id === next.defaultId,
    );
    const enabled = Boolean(
      defaultDefinition && defaultDefinition.active !== false && defaultDefinition.retired !== true,
    );
    await updateDomain(schoolId, domain.id, {
      parentMessages: next,
      defaultMessageActionId: null,
      defaultActionId: null,
      capabilities: buildDomainCapabilities({
        ...(domain.capabilities || {}),
        messages: enabled
          ? { enabled: true, delivery: 'automatic-event' }
          : { enabled: false },
      }),
    });
    setParentMessages(next);
    setActions(next.definitions);
    setDefaultId(next.defaultId);
    setSaveStatus('saved');
    window.setTimeout(() => setSaveStatus('idle'), 2200);
  };

  const save = async (event) => {
    event.preventDefault();
    setBusyId(editor.id || 'new');
    setError(null);
    try {
      const input = {
        label: editor.label,
        active: editor.active,
        sortOrder: editor.id
          ? (actions.find((action) => action.id === editor.id)?.sortOrder ?? 0)
          : actions.length,
        message: {
          subject: editor.subject,
          body: editor.body,
          buttons: editor.buttons,
        },
      };
      let next;
      let actionId = editor.id;
      if (editor.id) {
        next = updateDomainMessageAction(parentMessages, editor.id, input);
      } else {
        const created = createDomainMessageAction(parentMessages, input);
        next = created.parentMessages;
        actionId = created.actionId;
      }
      if (editor.makeDefault) {
        next = { ...next, defaultId: actionId };
      } else if (defaultId === actionId) {
        next = { ...next, defaultId: null };
      }
      await persist(next);
      setEditor(null);
    } catch (saveError) {
      setSaveStatus('error');
      setError(saveError.message || 'Failed to save parent message');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (action) => {
    setBusyId(action.id);
    setError(null);
    try {
      const current = readDomainMessageAction(action);
      const next = updateDomainMessageAction(parentMessages, action.id, {
        ...current,
        sortOrder: action.sortOrder ?? 0,
        active: action.active === false,
      });
      await persist(next);
    } catch (updateError) {
      setSaveStatus('error');
      setError(updateError.message || 'Failed to update parent message');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (action) => {
    setBusyId(action.id);
    setError(null);
    try {
      await persist(deleteDomainMessageAction(parentMessages, action.id));
    } catch (deleteError) {
      setSaveStatus('error');
      setError(deleteError.message || 'Failed to remove parent message');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="text-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">Parent communication</p>
            {saveStatus === 'saving' && <span className="badge badge-info badge-outline">Saving…</span>}
            {saveStatus === 'saved' && <span className="badge badge-success badge-outline">Saved</span>}
            {saveStatus === 'error' && <span className="badge badge-error badge-outline">Save failed</span>}
          </div>
          <p className="text-xs text-base-content/50 mt-0.5">
            Optional message copy and response buttons for newly recorded events. Queue follow-ups remain separate work.
          </p>
        </div>
        {!editor && (
          <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={() => setEditor({ ...EMPTY_EDITOR })}>
            <PlusIcon className="h-4 w-4" /> Add
          </button>
        )}
      </div>

      {error && <div className="alert alert-error py-2 text-sm mb-3"><span>{error}</span></div>}

      {editor ? (
        <Editor value={editor} busy={Boolean(busyId)} onChange={setEditor} onCancel={() => setEditor(null)} onSave={save} />
      ) : actions.length ? (
        <div className="space-y-2">
          {actions.map((action) => {
            const message = readDomainMessageAction(action).message;
            return (
              <div key={action.id} className="rounded-lg border border-base-300 p-3">
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square"
                    aria-label={action.active === false ? 'Activate' : 'Deactivate'}
                    onClick={() => toggleActive(action)}
                    disabled={busyId === action.id}
                  >
                    {action.active === false
                      ? <MinusCircleIcon className="h-4 w-4 text-base-content/30" />
                      : <CheckCircleIcon className="h-4 w-4 text-success" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={action.active === false ? 'line-through text-base-content/40' : 'font-medium'}>{action.label}</span>
                      {defaultId === action.id && <span className="badge badge-primary badge-xs">Used for new events</span>}
                    </div>
                    <p className="text-xs text-base-content/50 truncate mt-1">
                      {message.subject || message.body || 'No message copy'}
                    </p>
                    <p className="text-xs text-base-content/45 mt-1">
                      {message.buttons.length} response button{message.buttons.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square"
                    aria-label={`Edit ${action.label}`}
                    onClick={() => setEditor(actionEditorValue(action, defaultId))}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-error"
                    aria-label={`Remove ${action.label}`}
                    onClick={() => remove(action)}
                    disabled={busyId === action.id}
                  >
                    {busyId === action.id
                      ? <span className="loading loading-spinner loading-xs" />
                      : <XMarkIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-base-content/45 italic">No parent message is configured. This Domain stays silent.</p>
      )}
    </div>
  );
}
